// Shared helpers for the module:level RBAC model.
//
// A module role is a "<module>:<level>" string (e.g. "case:viewer"). Levels are
// additive within a module (admin ⊇ operator ⊇ viewer), so a role/key holds at
// most one level per module. The delegatable set of roles for the current user
// comes from GET /utility/moduleroles.
import { Color } from '@arritech/shared/types';

// Canonical module-role values. Mirrors the backend `rbac.ModuleRole` constants
// (arritech-base/rbac/roles.go) — the single source of truth for these strings.
// Gate on these everywhere via authStore.can(ModuleRole.X); never hardcode the
// raw "module:level" string.
export const ModuleRole = {
	CaseViewer: 'case:viewer',
	CaseOperator: 'case:operator',
	CaseAdmin: 'case:admin',

	AMLViewer: 'aml:viewer',
	AMLOperator: 'aml:operator',
	AMLAdmin: 'aml:admin',

	CFMViewer: 'cfm:viewer',
	CFMOperator: 'cfm:operator',
	CFMAdmin: 'cfm:admin',

	FormsViewer: 'forms:viewer',
	FormsOperator: 'forms:operator',
	FormsAdmin: 'forms:admin',

	POAViewer: 'poa:viewer',
	POAOperator: 'poa:operator',
	POAAdmin: 'poa:admin',

	PlatformUser: 'platform:user',
	PlatformOwner: 'platform:owner',
	PlatformPrimaryOwner: 'platform:primaryOwner',
} as const;

export type ModuleRole = (typeof ModuleRole)[keyof typeof ModuleRole];

// Retained system / auth claims. NOT module roles — they travel in the same flat
// claim array and are checked verbatim. Mirrors the backend `rbac` system-claim
// constants.
export const SystemClaim = {
	Root: 'Root',
	Agent: 'Agent',
	ServiceManagement: 'ServiceManagement',
	TwoFALogin: '2FALogin',
	TwoFASetup: '2FASetup',
} as const;

export type SystemClaim = (typeof SystemClaim)[keyof typeof SystemClaim];

export interface ModuleRoleOption {
	role: string; // "case:viewer"
	module: string; // "case"
	level: string; // "viewer"
}

export interface RolePreset {
	label: string; // already-translated label
	roles: string[]; // module:level strings
	icon?: string; // shared Icons constant, shown above the label as a card
	description?: string; // already-translated subtitle, shown under the label as a card
}

// Stable display order for modules; unknown modules are appended after these.
export const MODULE_ORDER = ['case', 'aml', 'cfm', 'forms', 'poa', 'platform'];

// The module that gates the shared operator shell: user/role listing, billing,
// notifications, API tokens, logs. Every merchant role has to carry a role from it
// (QGEN-5439), so it is named in a few places — never hardcode 'platform'.
export const PLATFORM_MODULE = 'platform';

// Badge tint per module, for the surfaces that render module roles as chips. Platform
// sits on every merchant role (QGEN-5439), so it takes the neutral slate and lets the
// feature modules carry the colour. Unknown modules fall back to grey rather than
// borrowing another module's tint.
const MODULE_COLORS: Record<string, Color> = {
	case: Color.PURPLE,
	aml: Color.ORANGE,
	cfm: Color.BLUE,
	forms: Color.AMBER,
	poa: Color.TEAL,
	[PLATFORM_MODULE]: Color.SLATE,
};

export const moduleColor = (module: string): Color =>
	MODULE_COLORS[module] ?? Color.GRAY;

// The merchant's ceiling role. Mirrors the stored `merchant_role.name`, which
// thanos-core force-assigns on every update, so the name is stable — and it has to
// be matched by name: `isDeletable` cannot serve as the marker, because every
// environment carries other non-deletable roles (`Agent` in prod, `Owner` + `Agent`
// in staging/dev), so the flag does not uniquely identify this one. Matched
// case-insensitively, so the guards share a single strategy.
export const GLOBAL_ADMIN_ROLE_NAME = 'Global Admin';
export const isGlobalAdminRole = (name?: string | null) =>
	name?.toLowerCase() === GLOBAL_ADMIN_ROLE_NAME.toLowerCase();

// Level precedence within a module, low to high.
export const LEVEL_ORDER: Record<string, number> = {
	viewer: 0,
	operator: 1,
	admin: 2,
	user: 0,
	owner: 1,
	primaryOwner: 2,
};

// Roles that may be granted to an API key (M2M). This is a frontend-only product
// constraint: the backend accepts any valid module role, but the integration API
// handlers only expose AML {operator, admin}, Case {viewer, operator} and
// CFM {operator}, so the key role picker is limited to this subset.
export const API_ASSIGNABLE_ROLES = [
	ModuleRole.AMLOperator,
	ModuleRole.AMLAdmin,
	ModuleRole.CaseViewer,
	ModuleRole.CaseOperator,
	ModuleRole.CFMOperator,
];

// The role a CFM catalogue write needs, by verb. Adding a risk level or a supported
// check is cfm:operator work; changing one that already exists is cfm:admin, and so
// are the deletes (gated separately). Mirrors the gateway guards on
// POST /cfm/{risk_level,check} versus POST /cfm/{risk_levels,check}/:id. There is no
// owner exception — an operator cannot edit even an entry it created itself.
export const cfmCatalogueWriteRole = (isExisting: boolean): ModuleRole =>
	isExisting ? ModuleRole.CFMAdmin : ModuleRole.CFMOperator;

export const moduleOf = (role: string): string => role.split(':')[0] ?? '';
export const levelOf = (role: string): string => role.split(':')[1] ?? '';

// Whether a set of held module roles satisfies a required "<module>:<level>"
// role. Levels are additive within a module (admin ⊇ operator ⊇ viewer,
// owner ⊇ user), so holding a higher level of the same module satisfies the
// requirement. The JWT carries the held module-role strings directly.
export const satisfiesModuleRole = (
	heldRoles: string[],
	required: string
): boolean => {
	const requiredModule = moduleOf(required);
	const requiredRank = LEVEL_ORDER[levelOf(required)];
	if (requiredRank === undefined) return false;
	return (heldRoles ?? []).some((held) => {
		if (moduleOf(held) !== requiredModule) return false;
		const heldRank = LEVEL_ORDER[levelOf(held)];
		return heldRank !== undefined && heldRank >= requiredRank;
	});
};

// Mirrors the backend `rbac.IsModuleRole`: whether a value is one of the module
// roles above, as opposed to a system claim or anything else travelling in the
// same flat claim array.
const MODULE_ROLE_SET = new Set<string>(Object.values(ModuleRole));
export const isModuleRole = (value: string): boolean =>
	MODULE_ROLE_SET.has(value);

// The one gate for both semantics. A module role is satisfied additively
// (admin ⊇ operator ⊇ viewer); anything else — a system claim — must match
// verbatim. Claims arrive reduced from the backend (QGEN-5462), carrying only the
// highest tier held per module, so `includes()` on a module role is no longer a
// valid membership test on its own: a token holding case:admin no longer lists
// case:viewer beside it.
export const satisfiesClaim = (held: string[], requirement: string): boolean =>
	isModuleRole(requirement)
		? satisfiesModuleRole(held, requirement)
		: (held ?? []).includes(requirement);

// user_claims may be an array (fresh from AUTH_SUCCESS) or a comma-joined string
// (rehydrated from sessionStorage). Normalize to a string array. Module-role
// strings use ':' as a separator, so splitting on ',' is safe.
export const toClaimArray = (value: unknown): string[] => {
	if (Array.isArray(value)) return value.map(String).filter(Boolean);
	if (typeof value === 'string') return value.split(',').filter(Boolean);
	return [];
};

// Order a list of module:level strings by module then level, for stable display.
export const sortModuleRoles = (roles: string[]): string[] =>
	[...roles].sort((a, b) => {
		const ma = MODULE_ORDER.indexOf(moduleOf(a));
		const mb = MODULE_ORDER.indexOf(moduleOf(b));
		const oa = ma === -1 ? MODULE_ORDER.length : ma;
		const ob = mb === -1 ? MODULE_ORDER.length : mb;
		if (oa !== ob) return oa - ob;
		if (moduleOf(a) !== moduleOf(b))
			return moduleOf(a).localeCompare(moduleOf(b));
		return (LEVEL_ORDER[levelOf(a)] ?? 0) - (LEVEL_ORDER[levelOf(b)] ?? 0);
	});

// Collapse a held set of module roles to the highest level per module. Mirrors the
// backend `rbac.ReduceModuleRoles` (arritech-base/rbac/roles.go): anything that is not
// a module role is dropped, the result is canonically ordered, and it is idempotent.
//
// Needed wherever the union of several merchant roles is shown as *effective* access:
// a raw union lists case:viewer beside case:admin, which reads as two separate grants
// when the admin tier already covers the viewer one.
export const reduceModuleRoles = (roles: string[]): string[] => {
	const rankOf = (level: string): number => LEVEL_ORDER[level] ?? -1;
	const highest = new Map<string, string>();

	for (const role of roles ?? []) {
		if (!isModuleRole(role)) continue;
		const current = highest.get(moduleOf(role));
		if (!current || rankOf(levelOf(role)) > rankOf(levelOf(current))) {
			highest.set(moduleOf(role), role);
		}
	}

	return sortModuleRoles([...highest.values()]);
};

// Pick one role per module — the highest level offered for it — out of a delegatable
// role list. Used to preselect the broadest permissions the caller may grant; for a
// Root caller that is case:admin, aml:admin, cfm:admin, forms:admin, poa:admin and
// platform:owner. Same collapse as the held-roles case, so it shares the one rule.
export const highestRolePerModule = (options: ModuleRoleOption[]): string[] =>
	reduceModuleRoles((options ?? []).map((option) => option.role));

// Append fallback only when the selection carries no platform role at all — one that
// already holds a platform role, at any level, keeps it.
export const ensurePlatformRole = (
	roles: string[],
	fallback: ModuleRole
): string[] => {
	const held = roles ?? [];
	return held.some((role) => moduleOf(role) === PLATFORM_MODULE)
		? [...held]
		: [...held, fallback];
};

// Replace whatever platform role the selection holds with the given one — for the
// places where the platform level is not the user's choice, such as the Global Admin
// role, which must stay platform:owner.
export const forcePlatformRole = (
	roles: string[],
	role: ModuleRole
): string[] => [
	...(roles ?? []).filter((held) => moduleOf(held) !== PLATFORM_MODULE),
	role,
];
